import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Table, Badge } from 'react-bootstrap';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  rewardBalance: number;
  isActive: boolean;
}

interface RewardTransaction {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  type: string;
  amount: number;
  description: string;
  createdAt: string;
  metadata?: {
    adminName?: string;
    adminEmail?: string;
    reason?: string;
    balanceBefore?: number;
    balanceAfter?: number;
  };
}

interface RewardStats {
  totalRewardsDistributed: number;
  totalActiveUsers: number;
  averageBalance: number;
  recentTransactions: number;
}

const RewardManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<RewardTransaction[]>([]);
  const [stats, setStats] = useState<RewardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  // Modal states
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusLoading, setBonusLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'users' | 'transactions' | 'stats'>('users');

  // Pagination states
  const [userPage, setUserPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('RewardManagement - Token:', token ? 'Present' : 'Missing');
      console.log('RewardManagement - API Base URL:', API_BASE_URL);
      
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch users with reward balances
      console.log('RewardManagement - Fetching users...');
      const usersResponse = await axios.get(`${API_BASE_URL}/admin/users?limit=1000`, config);
      console.log('RewardManagement - Users response:', usersResponse.data);
      
      if (!usersResponse.data.success) {
        console.error('RewardManagement - API returned unsuccessful response:', usersResponse.data);
        setUsers([]);
        return;
      }
      
      // Handle both array and object response formats
      const usersData = Array.isArray(usersResponse.data) ? usersResponse.data : usersResponse.data.users;
      console.log('RewardManagement - Parsed users data:', usersData);
      
      if (!usersData || usersData.length === 0) {
        console.warn('RewardManagement - No users found in response');
      }
      
      setUsers(usersData || []);

      // Fetch recent reward transactions
      let transactionsData: RewardTransaction[] = [];
      try {
        console.log('RewardManagement - Fetching transactions...');
        const transactionsResponse = await axios.get(`${API_BASE_URL}/admin/reward-transactions?limit=1000`, config);
        console.log('RewardManagement - Transactions response:', transactionsResponse.data);
        
        // Handle both array and object response formats
        transactionsData = Array.isArray(transactionsResponse.data) ? transactionsResponse.data : transactionsResponse.data.transactions || [];
        setTransactions(transactionsData);
      } catch (transactionError) {
        console.error('RewardManagement - Error fetching transactions:', transactionError);
        // Continue without transactions if endpoint fails
        setTransactions([]);
        transactionsData = [];
      }

      // Calculate stats
      const totalRewards = usersData.reduce((sum: number, user: User) => sum + (user.rewardBalance || 0), 0);
      const activeUsers = usersData.filter((user: User) => user.isActive).length;
      const avgBalance = activeUsers > 0 ? totalRewards / activeUsers : 0;
      const recentTrans = transactionsData.filter((t: RewardTransaction) => {
        const transDate = new Date(t.createdAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return transDate > weekAgo;
      }).length;

      setStats({
        totalRewardsDistributed: totalRewards,
        totalActiveUsers: activeUsers,
        averageBalance: avgBalance,
        recentTransactions: recentTrans
      });

    } catch (error: any) {
      console.error('Error fetching reward data:', error);
      console.error('Error response:', error.response?.data);
      
      // Set empty defaults on error
      setUsers([]);
      setTransactions([]);
      setStats({
        totalRewardsDistributed: 0,
        totalActiveUsers: 0,
        averageBalance: 0,
        recentTransactions: 0
      });
      
      // Show user-friendly error message
      if (error.response?.status === 401) {
        console.error('Authentication failed - redirecting to login');
        // Could add redirect to login here
      } else if (error.response?.status === 403) {
        console.error('Access denied - user may not have admin privileges');
      } else {
        console.error('Server error or network issue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddBonus = (user: User) => {
    setSelectedUser(user);
    setBonusAmount('');
    setBonusReason('');
    setShowBonusModal(true);
  };

  const handleBonusSubmit = async () => {
    if (!selectedUser || !bonusAmount || !bonusReason) return;

    setBonusLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/admin/users/${selectedUser._id}/bonus`, {
        amount: parseFloat(bonusAmount),
        reason: bonusReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowBonusModal(false);
      setBonusAmount('');
      setBonusReason('');
      setSelectedUser(null);
      fetchData(); // Refresh data
      
    } catch (error) {
      console.error('Error adding bonus:', error);
    } finally {
      setBonusLoading(false);
    }
  };


  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination for users
  const totalUserPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);

  // Pagination for transactions
  const totalTransactionPages = Math.ceil(transactions.length / itemsPerPage);
  const paginatedTransactions = transactions.slice((transactionPage - 1) * itemsPerPage, transactionPage * itemsPerPage);

  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;

  if (loading) {
    return (
      <div className="admin-card">
        <div className="admin-card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reward-management">
      <div className="admin-card">
        <div className="admin-card-header">
          <h5 className="mb-0"><i className="fas fa-gift me-2"></i>Reward Management</h5>
          <p className="mb-0 text-muted">
            <small>Manage user rewards, view transaction history, and monitor reward distribution statistics</small>
          </p>
        </div>
        
        <div className="admin-card-body">
          {/* Help Section */}
          <Alert variant="info" className="mb-4">
            <Alert.Heading as="h6">
              <i className="fas fa-info-circle me-2"></i>How to Use Reward Management
            </Alert.Heading>
            <ul className="mb-0 small">
              <li><strong>User Rewards:</strong> View and manage individual user reward balances, add manual bonuses</li>
              <li><strong>Transaction History:</strong> Track all reward transactions including task payments, referral bonuses, and manual additions</li>
              <li><strong>Statistics:</strong> Monitor overall reward distribution metrics and system health</li>
            </ul>
          </Alert>
          {/* Navigation Tabs */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users me-2"></i>User Rewards
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                <i className="fas fa-history me-2"></i>Transaction History
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                <i className="fas fa-chart-bar me-2"></i>Statistics
              </button>
            </li>
          </ul>

          {/* Statistics Tab */}
          {activeTab === 'stats' && stats && (
            <div>
              <Alert variant="success" className="mb-4">
                <strong>Statistics Overview:</strong> These metrics show the current state of rewards in your system and help track user engagement.
              </Alert>
              
              <div className="row">
                <div className="col-md-3 mb-3">
                  <div className="admin-card bg-primary text-white">
                    <div className="admin-card-body text-center">
                      <i className="fas fa-coins fa-2x mb-2"></i>
                      <h4 className="mb-0">{formatCurrency(stats.totalRewardsDistributed)}</h4>
                      <small>Total Rewards Distributed</small>
                      <div className="small mt-2 opacity-75">Sum of all user reward balances</div>
                    </div>
                  </div>
                </div>
              <div className="col-md-3 mb-3">
                <div className="admin-card bg-success text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-users fa-2x mb-2"></i>
                    <h4 className="mb-0">{stats.totalActiveUsers}</h4>
                    <small>Active Users</small>
                    <div className="small mt-2 opacity-75">Users with active accounts</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <div className="admin-card bg-info text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-chart-line fa-2x mb-2"></i>
                    <h4 className="mb-0">{formatCurrency(stats.averageBalance)}</h4>
                    <small>Average Balance</small>
                    <div className="small mt-2 opacity-75">Per active user average</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <div className="admin-card bg-warning text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-clock fa-2x mb-2"></i>
                    <h4 className="mb-0">{stats.recentTransactions}</h4>
                    <small>Recent Transactions (7 days)</small>
                    <div className="small mt-2 opacity-75">Activity in past week</div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="search-box">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setUserPage(1); // Reset to first page on search
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <Button variant="primary" onClick={() => fetchData()}>
                    <i className="fas fa-sync me-2"></i>Refresh
                  </Button>
                </div>
              </div>

              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Reward Balance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          {users.length === 0 ? (
                            <div>
                              <i className="fas fa-exclamation-triangle text-warning fa-2x mb-2"></i>
                              <tr>
                                <td colSpan={6} className="text-center py-4">
                                  <Alert variant="warning" className="mb-0">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    No users found. This could indicate database connection issues or no registered users.
                                  </Alert>
                                </td>
                              </tr>
                            </div>
                          ) : (
                            <div>
                              <i className="fas fa-search text-muted fa-2x mb-2"></i>
                              <div>No users match your search criteria.</div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map((user) => (
                        <tr key={user._id}>
                          <td>
                            <div className="user-info">
                              <strong>{user.name}</strong>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>{user.phone}</td>
                          <td>
                            <span className="reward-balance">
                              {formatCurrency(user.rewardBalance || 0)}
                            </span>
                          </td>
                          <td>
                            <Badge bg={user.isActive ? 'success' : 'danger'}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleAddBonus(user)}
                            >
                              <i className="fas fa-plus me-1"></i>Add Bonus
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls for Users */}
              {filteredUsers.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div className="text-muted small">
                    Showing {((userPage - 1) * itemsPerPage) + 1} to {Math.min(userPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
                  </div>
                  {totalUserPages > 1 && (
                    <div className="pagination-controls">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={userPage === 1}
                        onClick={() => setUserPage(1)}
                      >
                        <i className="fas fa-angle-double-left"></i>
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={userPage === 1}
                        onClick={() => setUserPage(userPage - 1)}
                      >
                        <i className="fas fa-angle-left me-1"></i>Previous
                      </Button>
                      <span className="mx-3">
                        Page <strong>{userPage}</strong> of <strong>{totalUserPages}</strong>
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        disabled={userPage === totalUserPages}
                        onClick={() => setUserPage(userPage + 1)}
                      >
                        Next<i className="fas fa-angle-right ms-1"></i>
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        disabled={userPage === totalUserPages}
                        onClick={() => setUserPage(totalUserPages)}
                      >
                        <i className="fas fa-angle-double-right"></i>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div>
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          <div>
                            <i className="fas fa-history text-muted fa-2x mb-2"></i>
                            <div>No reward transactions found.</div>
                            <small className="text-muted">Transactions will appear here after adding bonuses or when users complete referrals.</small>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedTransactions.map((transaction) => (
                        <tr key={transaction._id}>
                          <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                          <td>
                            <div>
                              <strong>{transaction.userId?.name || 'Unknown User'}</strong>
                              <br />
                              <small className="text-muted">{transaction.userId?.email || 'No email'}</small>
                            </div>
                          </td>
                          <td>
                            <Badge 
                              bg={transaction.type === 'bonus' ? 'success' : 
                                  transaction.type === 'referral' ? 'info' : 'secondary'}
                            >
                              {transaction.type}
                            </Badge>
                          </td>
                          <td>
                            <span className={`text-${transaction.amount > 0 ? 'success' : 'danger'}`}>
                              {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td>{transaction.description}</td>
                          <td>
                            {transaction.metadata?.adminName ? (
                              <small className="text-muted">
                                {transaction.metadata.adminName}
                              </small>
                            ) : (
                              <small className="text-muted">System</small>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls for Transactions */}
              {transactions.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div className="text-muted small">
                    Showing {((transactionPage - 1) * itemsPerPage) + 1} to {Math.min(transactionPage * itemsPerPage, transactions.length)} of {transactions.length} transactions
                  </div>
                  {totalTransactionPages > 1 && (
                    <div className="pagination-controls">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={transactionPage === 1}
                        onClick={() => setTransactionPage(1)}
                      >
                        <i className="fas fa-angle-double-left"></i>
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={transactionPage === 1}
                        onClick={() => setTransactionPage(transactionPage - 1)}
                      >
                        <i className="fas fa-angle-left me-1"></i>Previous
                      </Button>
                      <span className="mx-3">
                        Page <strong>{transactionPage}</strong> of <strong>{totalTransactionPages}</strong>
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        disabled={transactionPage === totalTransactionPages}
                        onClick={() => setTransactionPage(transactionPage + 1)}
                      >
                        Next<i className="fas fa-angle-right ms-1"></i>
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        disabled={transactionPage === totalTransactionPages}
                        onClick={() => setTransactionPage(totalTransactionPages)}
                      >
                        <i className="fas fa-angle-double-right"></i>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Bonus Modal */}
      <Modal show={showBonusModal} onHide={() => setShowBonusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-plus-circle me-2"></i>Add Bonus Reward
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <div className="mb-3">
                <strong>User:</strong> {selectedUser.name} ({selectedUser.email})
              </div>
              <div className="mb-3">
                <strong>Current Balance:</strong> {formatCurrency(selectedUser.rewardBalance || 0)}
              </div>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Bonus Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(e.target.value)}
                    placeholder="Enter bonus amount"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={bonusReason}
                    onChange={(e) => setBonusReason(e.target.value)}
                    placeholder="Enter reason for bonus"
                  />
                </Form.Group>
              </Form>

              {bonusAmount && bonusReason && selectedUser && (
                <Alert variant="info">
                  <h6><i className="fas fa-info-circle me-2"></i>Confirmation Required</h6>
                  <p className="mb-1"><strong>Action:</strong> Add ₹{bonusAmount} bonus to {selectedUser.name}</p>
                  <p className="mb-1"><strong>Current Balance:</strong> ₹{(selectedUser.rewardBalance || 0).toFixed(2)}</p>
                  <p className="mb-1"><strong>New Balance:</strong> ₹{((selectedUser.rewardBalance || 0) + parseFloat(bonusAmount || '0')).toFixed(2)}</p>
                  <p className="mb-0"><strong>Audit Log:</strong> This action will be permanently logged with your admin credentials.</p>
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBonusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleBonusSubmit}
            disabled={!bonusAmount || !bonusReason || bonusLoading}
          >
            {bonusLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>Add Bonus
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RewardManagement;